import React, { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, AlertTriangle, CheckCircle, Clock, X, Plus, Info } from "lucide-react";
import CompOffEmployee from './CompOffEmployee';
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../services/config';
const API = apiBaseUrl;

const ax  = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

export default function EmployeeLeaves() {
  const { T, theme } = useTheme();

  const STATUS_COLOR = {
    Approved:  { bg:`${T.green}1A`, color:T.green },
    Pending:   { bg:`${T.amber}1A`, color:T.amber },
    Rejected:  { bg:`${T.red}1A`,   color:T.red },
    Cancelled: { bg:`${T.muted}1A`, color:T.muted },
  };

  const userData   = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
  const platformId = userData.id;
  const employeeId = userData.employee_id;

  const [activeTab, setActiveTab] = useState("leaves");
  const [balances,  setBalances]  = useState([]);
  const [requests,  setRequests]  = useState([]);
  const [leaveTypes,setLeaveTypes]= useState([]);
  const [loading,   setLoading]   = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [toast,       setToast]       = useState("");
  const [form,        setForm]        = useState({
    leave_type_id:"", start_date:"", end_date:"", reason:"", is_half_day:false
  });
  const [correctReq,  setCorrectReq]  = useState(null);   // request being corrected
  const [correctForm, setCorrectForm] = useState({});      // correction form data
  const [correcting,  setCorrecting]  = useState(false);

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""),4000); };

  const load = async () => {
    if (!platformId) return;
    try {
      setLoading(true);
      const [bRes, rRes, ltRes] = await Promise.all([
        axios.get(`${API}/employee-portal/leave-balances?platform_user_id=${platformId}`, ax()),
        axios.get(`${API}/employee-portal/leave-requests?platform_user_id=${platformId}`, ax()),
        axios.get(`${API}/leave-types/`, ax()),
      ]);
      setBalances(bRes.data   || []);
      setRequests(rRes.data   || []);
      setLeaveTypes(ltRes.data|| []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [platformId]);

  const selBalance = balances.find(b => String(b.leave_type_id) === String(form.leave_type_id));
  const selType    = leaveTypes.find(lt => String(lt.id) === String(form.leave_type_id));

  const requestedDays = (() => {
    if (!form.start_date || !form.end_date) return 0;
    if (form.is_half_day) return 0.5;
    const s = new Date(form.start_date), e = new Date(form.end_date);
    if (e < s) return 0;
    let days = 0, cur = new Date(s);
    while (cur <= e) { if (cur.getDay() !== 0 && cur.getDay() !== 6) days++; cur.setDate(cur.getDate()+1); }
    return days;
  })();

  // Cast to Number — API may return remaining as string
  const remaining   = Number(selBalance?.remaining || 0);
  const allocated   = Number(selBalance?.allocated || 0);
  const willBeLOP   = selBalance && requestedDays > 0 && remaining < requestedDays;

  // Exact LOP split:
  //   balanceDays = days covered by existing balance (e.g. 10)
  //   lopDays     = days with no balance — will be deducted from salary (e.g. 1)
  const balanceDays = willBeLOP ? remaining : requestedDays;
  const lopDays     = willBeLOP ? Math.round((requestedDays - remaining) * 10) / 10 : 0;

  const submitLeave = async () => {
    if (!form.leave_type_id || !form.start_date || !form.end_date || !form.reason) {
      flash("❌ Please fill all fields"); return;
    }
    setSubmitting(true);
    try {
      // ── CHANGE 1: Include is_lop + lop_days in payload ──────────
      // Previously is_lop was never sent — backend never knew to mark LOP.
      // Now: if balance is insufficient, is_lop=true and lop_days=excess days.
      const payload = {
        ...form,
        leave_type_id: parseInt(form.leave_type_id),
        is_lop:        willBeLOP,
        lop_days:      lopDays,
      };
      const r = await axios.post(
        `${API}/leave-requests/apply?platform_user_id=${platformId}`,
        payload, ax()
      );
      flash(r.data.message || "✅ Leave applied successfully");
      setApplyOpen(false);
      setForm({ leave_type_id:"", start_date:"", end_date:"", reason:"", is_half_day:false });
      load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error")); }
    finally { setSubmitting(false); }
  };

  const openCorrect = (req) => {
    setCorrectReq(req);
    setCorrectForm({
      start_date: req.start_date,
      end_date:   req.end_date,
      reason:     req.reason || "",
      is_half_day:req.is_half_day || false,
    });
  };

  const submitCorrection = async () => {
    if (!correctForm.start_date || !correctForm.end_date || !correctForm.reason) {
      flash("❌ Please fill all fields"); return;
    }
    setCorrecting(true);
    try {
      await axios.put(
        `${API}/leave-requests/${correctReq.id}/correct?platform_user_id=${platformId}`,
        correctForm, ax()
      );
      flash("✅ Leave request updated");
      setCorrectReq(null);
      load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Could not update")); }
    finally { setCorrecting(false); }
  };

  const cancelRequest = async (reqId) => {
    try {
      await axios.put(`${API}/leave-requests/${reqId}/cancel?platform_user_id=${platformId}`, {}, ax());
      flash("✅ Leave request cancelled");
      load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Cannot cancel")); }
  };

  const card = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12 };
  const inp  = { width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" };
  const lbl  = { fontSize:11, color:T.muted, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.07em" };

  if (loading) return <div style={{ padding:60, textAlign:"center", color:T.muted, fontFamily:"Outfit,sans-serif" }}>Loading…</div>;

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>

      {/* ── Page header ── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
        <div>
          <div style={{ fontSize:11, color:T.cyan, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>ATTENDANCE &amp; LEAVE</div>
          <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>My Leaves</h2>
          <p style={{ fontSize:13, color:T.muted, margin:0 }}>Apply leave, track balance and manage comp-off</p>
        </div>
        {activeTab === "leaves" && (
          <button onClick={() => setApplyOpen(true)}
            style={{ display:"flex", alignItems:"center", gap:7, background:`linear-gradient(135deg,${T.cyan},${T.purple})`, border:"none", borderRadius:9, color:"#fff", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700, padding:"10px 18px", cursor:"pointer" }}>
            <Plus size={14}/> Apply Leave
          </button>
        )}
      </div>

      {/* ── Tab switcher ── */}
      <div style={{ display:"flex", gap:6, borderBottom:`1px solid ${T.border}`, marginBottom:22 }}>
        {[
          { key:"leaves",  icon:<Calendar size={14}/>, label:"Leave Requests" },
          { key:"compoff", icon:<Clock    size={14}/>, label:"Comp-Off"        },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              display:"inline-flex", alignItems:"center", gap:7,
              padding:"9px 18px",
              borderRadius:"8px 8px 0 0",
              border:`1px solid ${activeTab===t.key?T.border_strong:"transparent"}`,
              borderBottom:"none",
              background: activeTab===t.key ? T.card : "transparent",
              color: activeTab===t.key ? T.cyan : T.muted,
              cursor:"pointer", fontFamily:"Outfit,sans-serif",
              fontSize:13, fontWeight:700,
              marginBottom: activeTab===t.key ? -1 : 0,
            }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ══ LEAVE REQUESTS TAB ══════════════════════════════════ */}
      {activeTab === "leaves" && (
        <>
          {/* Balance cards */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:12, marginBottom:22 }}>
            {balances.filter(b => b.allocated > 0 || b.leave_type === "Loss of Pay").map(b => {
              const pct  = b.allocated > 0 ? Math.round((b.used / b.allocated) * 100) : 0;
              const warn = b.remaining <= 2 && b.allocated > 0;
              return (
                <div key={b.id} style={{ ...card, padding:16, borderTop:`3px solid ${b.color||T.cyan}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:T.text, lineHeight:1.3 }}>{b.leave_type}</div>
                    {warn && <AlertTriangle size={13} style={{ color:T.amber, flexShrink:0 }}/>}
                  </div>
                  <div style={{ fontSize:26, fontWeight:900, fontFamily:"JetBrains Mono,monospace", color:warn?T.amber:T.text, lineHeight:1 }}>
                    {b.remaining}
                  </div>
                  <div style={{ fontSize:10.5, color:T.muted, marginBottom:8 }}>of {b.allocated} days remaining</div>
                  {b.allocated > 0 && (
                    <div style={{ height:4, background:T.surface_med, borderRadius:4, overflow:"hidden" }}>
                      <div style={{ width:`${pct}%`, height:"100%", background:warn?T.amber:b.color||T.cyan, borderRadius:4, transition:"width 0.4s" }}/>
                    </div>
                  )}
                  <div style={{ marginTop:6, fontSize:10.5, padding:"2px 7px", borderRadius:8, display:"inline-block",
                    background:b.is_paid?`${T.green}14`:`${T.red}14`,
                    color:b.is_paid?T.green:T.red }}>
                    {b.is_paid?"Paid":"Unpaid"}
                  </div>
                </div>
              );
            })}
            {balances.length === 0 && (
              <div style={{ ...card, padding:24, gridColumn:"1/-1", textAlign:"center", color:T.muted, fontSize:13 }}>
                No leave balances assigned yet. Contact HR.
              </div>
            )}
          </div>

          {/* LOP note */}
          <div style={{ ...card, padding:"12px 18px", marginBottom:16, display:"flex", gap:10, alignItems:"center", background:`${T.red}0A`, borderColor:`${T.red}1F` }}>
            <Info size={14} style={{ color:T.red, flexShrink:0 }}/>
            <span style={{ fontSize:12.5, color:T.muted }}>
              If you apply leave when your balance is exhausted, it will be automatically marked as <strong style={{ color:T.red }}>Loss of Pay (LOP)</strong> and deducted from your salary.
            </span>
          </div>

          {/* Request history */}
          <div style={card}>
            <div style={{ padding:"14px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600 }}>Leave History</h3>
              <span style={{ fontSize:12, color:T.muted }}>{requests.length} requests</span>
            </div>
            {requests.length === 0 ? (
              <div style={{ padding:40, textAlign:"center", color:T.muted, fontSize:13 }}>No leave requests yet</div>
            ) : (
              <div>
                {requests.map(r => {
                  const sc = STATUS_COLOR[r.status] || STATUS_COLOR.Pending;
                  return (
                    <div key={r.id} style={{ padding:"14px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", gap:14, alignItems:"center" }}>
                      <div style={{ width:10, height:10, borderRadius:"50%", background:r.leave_color||T.cyan, flexShrink:0 }}/>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:3 }}>
                          <span style={{ fontSize:13, fontWeight:600 }}>{r.leave_type}</span>
                          {r.is_lop && <span style={{ fontSize:10.5, padding:"2px 7px", borderRadius:8, background:`${T.red}1A`, color:T.red, fontWeight:600 }}>LOP</span>}
                        </div>
                        <div style={{ fontSize:12, color:T.muted }}>
                          {r.start_date} → {r.end_date} · {r.days} day{r.days!==1?"s":""}
                          {r.is_lop && <span style={{ color:T.red, marginLeft:6 }}>· LOP</span>}
                        </div>
                        {r.reason  && <div style={{ fontSize:12, color:T.dim, marginTop:2 }}>{r.reason}</div>}
                        {r.remarks && <div style={{ fontSize:12, color:T.amber, marginTop:2 }}>HR: {r.remarks}</div>}
                      </div>
                      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                        <span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20, fontWeight:600, background:sc.bg, color:sc.color }}>
                          {r.status}
                        </span>
                        {r.status === "Pending" && (
                          <div style={{ display:"flex", gap:5 }}>
                            <button onClick={() => openCorrect(r)}
                              style={{ background:"none", border:`1px solid ${T.border_strong}`, borderRadius:7, color:T.cyan, fontSize:11.5, padding:"3px 9px", cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>
                              ✏️ Edit
                            </button>
                            <button onClick={() => cancelRequest(r.id)}
                              style={{ background:"none", border:`1px solid ${T.red}33`, borderRadius:7, color:T.red, fontSize:11.5, padding:"3px 9px", cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* ══ COMP-OFF TAB ══════════════════════════════════════ */}
      {activeTab === "compoff" && (
        <CompOffEmployee employeeId={employeeId} />
      )}

      {/* ── Apply Leave modal ── */}
      {applyOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Outfit,sans-serif" }}
          onClick={() => setApplyOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:480, overflow:"hidden" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Apply Leave</h3>
              <button onClick={() => setApplyOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={16}/></button>
            </div>
            <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>

              <div>
                <label style={lbl}>Leave Type *</label>
                <select value={form.leave_type_id} onChange={e=>setForm(p=>({...p,leave_type_id:e.target.value}))} style={inp}>
                  <option value="">Select leave type…</option>
                  {leaveTypes.filter(lt=>lt.is_active).map(lt => {
                    const bal = balances.find(b=>b.leave_type_id===lt.id);
                    const rem = bal?.remaining ?? "—";
                    return (
                      <option key={lt.id} value={lt.id} style={{ background:T.opt_bg }}>
                        {lt.name} — {rem} days remaining
                      </option>
                    );
                  })}
                </select>
              </div>

              {selBalance && (
                <div style={{
                  padding:"10px 14px", borderRadius:9, fontSize:13, display:"flex", gap:10, alignItems:"center",
                  background: selBalance.remaining > 0 ? `${T.green}0F` : `${T.red}0F`,
                  border: `1px solid ${selBalance.remaining > 0 ? `${T.green}26` : `${T.red}26`}`,
                }}>
                  {selBalance.remaining > 0
                    ? <CheckCircle size={14} style={{ color:T.green, flexShrink:0 }}/>
                    : <AlertTriangle size={14} style={{ color:T.red, flexShrink:0 }}/>}
                  <span style={{ color: selBalance.remaining > 0 ? T.green : T.red }}>
                    {remaining <= 0
                      ? "⚠️ No balance — full leave will be Loss of Pay (deducted from salary)"
                      : requestedDays > 0 && willBeLOP
                        ? `${remaining} days remaining · using ${balanceDays} day(s) from balance + ${lopDays} day(s) LOP`
                        : `${remaining} days remaining of ${allocated} allocated`}
                  </span>
                </div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>
                  <label style={lbl}>Start Date *</label>
                  <input type="date" value={form.start_date} onChange={e=>setForm(p=>({...p,start_date:e.target.value}))} style={{ ...inp, colorScheme:theme }}/>
                </div>
                <div>
                  <label style={lbl}>End Date *</label>
                  <input type="date" value={form.end_date} min={form.start_date} onChange={e=>setForm(p=>({...p,end_date:e.target.value}))} style={{ ...inp, colorScheme:theme }}/>
                </div>
              </div>

              <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                <label style={{ display:"flex", alignItems:"center", gap:7, cursor:"pointer", fontSize:13, color:T.muted }}>
                  <input type="checkbox" checked={form.is_half_day}
                    onChange={e=>setForm(p=>({...p,is_half_day:e.target.checked}))}
                    disabled={selType && !selType.allow_half_day}
                    style={{ accentColor:T.cyan }}/>
                  Half Day
                  {selType && !selType.allow_half_day && <span style={{ fontSize:11, color:T.dim }}>(not allowed)</span>}
                </label>
                {requestedDays > 0 && (
                  <div style={{ fontSize:12.5, padding:"4px 12px", borderRadius:8, background:`${T.cyan}14`, color:T.cyan }}>
                    {requestedDays} working day{requestedDays!==1?"s":""}
                    {willBeLOP && <span style={{ color:T.red, marginLeft:6 }}>· {lopDays} day{lopDays!==1?"s":""} LOP</span>}
                  </div>
                )}
              </div>

              <div>
                <label style={lbl}>Reason *</label>
                <textarea value={form.reason} onChange={e=>setForm(p=>({...p,reason:e.target.value}))}
                  rows={3} placeholder="Reason for leave…"
                  style={{ ...inp, resize:"vertical" }}/>
              </div>

              {willBeLOP && (
                <div style={{ padding:"10px 14px", borderRadius:9, background:`${T.red}0F`, border:`1px solid ${T.red}26`, fontSize:12.5, color:T.red, display:"flex", gap:8 }}>
                  <AlertTriangle size={14} style={{ flexShrink:0, marginTop:1 }}/>
                  <span>
                    You have <strong>{remaining}</strong> day(s) remaining.
                    Requesting <strong>{requestedDays}</strong> days —
                    <strong> {balanceDays}</strong> day(s) from balance,
                    <strong style={{ color:T.red }}> {lopDays}</strong> day(s) will be <strong>Loss of Pay</strong> (deducted from salary).
                  </span>
                </div>
              )}

              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={()=>setApplyOpen(false)}
                  style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${T.border}`, background:T.toggle_bg, color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>
                  Cancel
                </button>
                <button onClick={submitLeave} disabled={submitting}
                  style={{ padding:"9px 18px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${willBeLOP?`${T.red},${T.amber}`:`${T.cyan},${T.purple}`})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700, opacity:submitting?0.7:1 }}>
                  {submitting ? "Submitting…" : willBeLOP ? `Submit (${lopDays} day${lopDays!==1?"s":""} LOP)` : "Submit Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Correction / Edit Modal ── */}
      {correctReq && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998,
          display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Outfit,sans-serif" }}
          onClick={() => setCorrectReq(null)}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}33`,
            borderRadius:14, width:480, overflow:"hidden" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`,
              background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Edit Leave Request</h3>
                <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>
                  {correctReq.leave_type} · Originally: {correctReq.start_date} → {correctReq.end_date}
                </div>
              </div>
              <button onClick={() => setCorrectReq(null)}
                style={{ background:"none", border:"none", cursor:"pointer", color:T.muted, fontSize:18 }}>✕</button>
            </div>
            <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>

              <div style={{ background:`${T.cyan}0D`, border:`1px solid ${T.cyan}1F`,
                borderRadius:8, padding:"10px 14px", fontSize:12.5, color:T.muted }}>
                ℹ️ You can correct dates and reason for a <strong style={{ color:T.text }}>Pending</strong> leave request.
                The manager will review the updated request.
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>
                  <label style={lbl}>Start Date *</label>
                  <input type="date" value={correctForm.start_date}
                    onChange={e => setCorrectForm(p => ({...p, start_date:e.target.value}))}
                    style={{ ...inp, colorScheme:theme }}/>
                </div>
                <div>
                  <label style={lbl}>End Date *</label>
                  <input type="date" value={correctForm.end_date}
                    min={correctForm.start_date}
                    onChange={e => setCorrectForm(p => ({...p, end_date:e.target.value}))}
                    style={{ ...inp, colorScheme:theme }}/>
                </div>
              </div>

              <div>
                <label style={{ display:"flex", alignItems:"center", gap:7, cursor:"pointer",
                  fontSize:13, color:T.muted }}>
                  <input type="checkbox" checked={correctForm.is_half_day || false}
                    onChange={e => setCorrectForm(p => ({...p, is_half_day:e.target.checked}))}
                    style={{ accentColor:T.cyan }}/>
                  Half Day
                </label>
              </div>

              <div>
                <label style={lbl}>Reason *</label>
                <textarea value={correctForm.reason}
                  onChange={e => setCorrectForm(p => ({...p, reason:e.target.value}))}
                  rows={3} placeholder="Reason for leave…"
                  style={{ ...inp, resize:"vertical" }}/>
              </div>

              <div style={{ display:"flex", gap:10, justifyContent:"flex-end",
                paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={() => setCorrectReq(null)}
                  style={{ padding:"9px 18px", borderRadius:8,
                    border:`1px solid ${T.border}`, background:T.toggle_bg,
                    color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>
                  Cancel
                </button>
                <button onClick={submitCorrection} disabled={correcting}
                  style={{ padding:"9px 18px", borderRadius:8, border:"none",
                    background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                    color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif",
                    fontSize:13, fontWeight:700, opacity:correcting?0.7:1 }}>
                  {correcting ? "Saving…" : "Save Correction"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", padding:"10px 22px", borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card, border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red, boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}